import React, { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl-next';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { useFlags } from '@atlaskit/flag';
import ErrorIcon from '@atlaskit/icon/core/migration/error';
import CheckCircleIcon from '@atlaskit/icon/core/migration/success--check-circle';
import { G300, R300 } from '@atlaskit/theme/colors';
import { fireTrackAnalytics } from '@atlassian/analytics-bridge';
import { FormStatus } from '@atlassian/proforma-common-core/form-system-models';
import { Action, ActionSubject, FormUpdateType } from '@atlassian/proforma-utils/analytics';
import { getSimpleFormAnalytics } from '../../../utils/simple-form-analytics';
import { useReportError, useSelectedFormStoreState } from '../store';
import { messages } from './messages';
import { ErrorType, SubmitError, SubmitResult } from './types';
import { useSaveModifiedFormWithErrorFlag } from './utils/useSaveModifiedFormWithErrorFlag';
import { useSubmitForm } from './utils/useSubmitForm';
import { validateForm } from './utils/validateForm';
export const useHandleSubmitForm = projectType => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    selectedFormStore
  } = useSelectedFormStoreState();
  const {
    saveModifiedFormWithErrorFlag
  } = useSaveModifiedFormWithErrorFlag();
  const {
    submitFormWithConfirmation,
    submitForm
  } = useSubmitForm(projectType);
  const reportError = useReportError();
  const {
    showFlag
  } = useFlags();
  const {
    createAnalyticsEvent
  } = useAnalyticsEvents();
  const handleError = useCallback(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  e => {
    if (!(e instanceof SubmitError)) {
      reportError(e, {
        id: 'submitForm',
        attributes: {
          errorType: 'unknown'
        }
      });
      return SubmitResult.UnknownError;
    }
    switch (e.type) {
      case ErrorType.Save:
        // Do not showFlag() or reportError() for save, because saveModifiedFormWithErrorFlag already did it
        return SubmitResult.SaveError;
      case ErrorType.Validation:
        //  Do not report error for validation error
        showFlag({
          title: /*#__PURE__*/React.createElement(FormattedMessage, messages.validationFailedFlagTitle),
          description: /*#__PURE__*/React.createElement(FormattedMessage, messages.validationFailedFlagDescription),
          icon: /*#__PURE__*/React.createElement(ErrorIcon, {
            spacing: "spacious",
            label: "Error",
            color: `var(--ds-icon-danger, ${R300})`
          })
        });
        return SubmitResult.ValidationError;
      case ErrorType.Submit:
        reportError(e.error, {
          id: 'submitForm',
          attributes: {
            errorType: e.type
          }
        });
        showFlag({
          title: /*#__PURE__*/React.createElement(FormattedMessage, messages.submitFailedFlagTitle),
          description: /*#__PURE__*/React.createElement(FormattedMessage, messages.submitFailedFlagDescription),
          icon: /*#__PURE__*/React.createElement(ErrorIcon, {
            spacing: "spacious",
            label: "Error",
            color: `var(--ds-icon-danger, ${R300})`
          })
        });
        return SubmitResult.SubmitError;
      default:
        return SubmitResult.UnknownError;
    }
  }, [reportError, showFlag]);
  return {
    isSubmitting,
    handleSubmitForm: useCallback(async () => {
      if (!selectedFormStore) {
        return;
      }
      try {
        setIsSubmitting(true);
        let submitResult;
        await validateForm(selectedFormStore);
        await saveModifiedFormWithErrorFlag(selectedFormStore);
        if (selectedFormStore.status === FormStatus.Open) {
          submitResult = selectedFormStore.internal || projectType !== 'service_desk' ? await submitForm(selectedFormStore) : await submitFormWithConfirmation(selectedFormStore);
        } else {
          submitResult = await submitForm(selectedFormStore);
        }
        if (submitResult === SubmitResult.Success) {
          showFlag({
            title: /*#__PURE__*/React.createElement(FormattedMessage, messages.submitSuccessFlagTitle),
            isAutoDismiss: true,
            icon: /*#__PURE__*/React.createElement(CheckCircleIcon, {
              spacing: "spacious",
              label: "Success",
              color: `var(--ds-icon-success, ${G300})`
            })
          });
          fireTrackAnalytics(createAnalyticsEvent({}), `${ActionSubject.FORM} ${Action.UPDATED}`, {
            formUpdateType: FormUpdateType.SUBMIT,
            ...getSimpleFormAnalytics(selectedFormStore.formId, selectedFormStore.templateFormId, selectedFormStore.status, selectedFormStore.internal)
          });
        }
        return submitResult;
      } catch (e) {
        return handleError(e);
      } finally {
        setIsSubmitting(false);
      }
    }, [selectedFormStore, saveModifiedFormWithErrorFlag, projectType, submitForm, submitFormWithConfirmation, showFlag, createAnalyticsEvent, handleError])
  };
};